import { useState, useEffect } from 'react';

// Where the "you are here" line sits, as a fraction of the viewport height
const LINE = 0.4;

/**
 * Returns the id of the section currently being read: the last one whose top has passed a line
 * 40% down the viewport. Returns null above the first section (e.g. on the hero).
 * The last section may never reach the line, so the bottom of the page counts as inside it.
 */
const useActiveSection = (ids, { enabled = true } = {}) => {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const sections = ids.map(id => document.getElementById(id)).filter(Boolean);
    if (!sections.length) {
      return;
    }

    let ticking = false;

    const update = () => {
      ticking = false;
      const isAtBottom =
        window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;
      if (isAtBottom) {
        setActiveId(sections[sections.length - 1].id);
        return;
      }
      const line = window.innerHeight * LINE;
      const passed = sections.filter(section => section.getBoundingClientRect().top <= line);
      setActiveId(passed.length ? passed[passed.length - 1].id : null);
    };

    // At most one check per frame, same as useScrollDirection
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, ids.join()]);

  return activeId;
};

export default useActiveSection;
